import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow, Badge, Button } from 'flowbite-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import Modals from '../../components/Modal';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [selectedDeliverable, setSelectedDeliverable] = useState(null);
  const [selectedDeliverableName, setSelectedDeliverableName] = useState('');
  const [history, setHistory] = useState([]);
  const [progress, setProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [projectResp, delivResp, progressResp] = await Promise.all([
          axios.get(
            `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/projects/${projectId}`,
            { withCredentials: true },
          ),
          axios.get(
            `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/project/${projectId}/deliverables`,
            { withCredentials: true },
          ),
          axios.get(
            `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/project/${projectId}/progress`,
            { withCredentials: true },
          ),
        ]);
        setProject(projectResp.data);
        setDeliverables(delivResp.data);
        setProgress(progressResp.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [projectId]);

  const loadHistory = async (pd) => {
    setSelectedDeliverable(pd.id);
    setSelectedDeliverableName(pd.deliverable?.name || '');
    setIsLoading(true);
    try {
      const resp = await axios.get(
        `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/deliverable/${pd.id}/history`,
        { withCredentials: true },
      );
      setHistory(resp.data);
      setIsHistoryModalOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !project) {
    return <LoadingSpinner message="Cargando proyecto..." size="lg" color="blue" />;
  }

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  if (!project) {
    return null;
  }

  const formatPercent = (value) => {
    if (value == null) return '0%';
    const n = Number.isNaN(Number(value)) ? 0 : Number(value);
    return `${n}%`;
  };

  const stagesGrouped = deliverables.reduce((acc, pd) => {
    const stage = pd.deliverable?.stage || null;
    const key = stage ? String(stage.id) : 'none';
    if (!acc[key]) {
      acc[key] = {
        stage,
        items: [],
      };
    }
    acc[key].items.push(pd);
    return acc;
  }, {});

  const stageProgressMap = {};
  if (progress && Array.isArray(progress.stages)) {
    progress.stages.forEach((s) => {
      const key = s.stageId != null ? String(s.stageId) : 'none';
      stageProgressMap[key] = s;
    });
  }

  return (
    <div className="p-4 mt-4 space-y-4">
      {isLoading && <LoadingSpinner message="Procesando..." size="md" color="blue" />}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold mb-1">
            Proyecto de {project.Client?.business_name || 'Cliente'}
          </h1>
          <p className="text-sm text-gray-600">
            Tipo de proyecto: <span className="font-medium">{project.project_type}</span>
          </p>
          {progress && (
            <p className="text-sm text-gray-600 mt-1">
              Avance del proyecto:{' '}
              <span className="font-semibold text-emerald-600">
                {formatPercent(progress.percent)}
              </span>
            </p>
          )}
        </div>
        <Button color="light" onClick={() => navigate(-1)}>
          Volver
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <h2 className="text-lg font-semibold mb-2">Resumen del proyecto</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div>
            <p><span className="font-semibold">Cliente:</span> {project.Client?.business_name}</p>
            <p><span className="font-semibold">RUT:</span> {project.Client?.rut || 'N/D'}</p>
            <p><span className="font-semibold">Área:</span> {project.area}</p>
          </div>
          <div>
            <p>
              <span className="font-semibold">Fecha inicio:</span>{' '}
              {project.start_date ? new Date(project.start_date).toLocaleDateString() : 'N/D'}
            </p>
            <p>
              <span className="font-semibold">Go Live:</span>{' '}
              {project.go_live_date ? new Date(project.go_live_date).toLocaleDateString() : 'Pendiente'}
            </p>
            <p>
              <span className="font-semibold">Cierre:</span>{' '}
              {project.end_date ? new Date(project.end_date).toLocaleDateString() : 'Abierto'}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-4">
        <h2 className="text-lg font-semibold mb-2">Entregables del proyecto por etapa</h2>

        {Object.keys(stagesGrouped).length === 0 && (
          <p className="text-sm text-gray-500">No hay entregables asociados a este proyecto.</p>
        )}

        {Object.entries(stagesGrouped).map(([key, group]) => {
          const stage = group.stage;
          const progressStage = stageProgressMap[key];
          return (
            <div key={key} className="border border-gray-100 rounded-lg mb-3">
              <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {stage ? stage.name : 'Sin etapa'}
                  </p>
                  {stage?.description && (
                    <p className="text-xs text-gray-500">{stage.description}</p>
                  )}
                </div>
                {progressStage && (
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Avance de la etapa</p>
                    <p className="text-sm font-semibold text-emerald-600">
                      {formatPercent(progressStage.percent)}
                    </p>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHead>
                    <TableHeadCell>Entregable</TableHeadCell>
                    <TableHeadCell>Tipo proyecto</TableHeadCell>
                    <TableHeadCell>
                      <span className="srOnly">Acciones</span>
                    </TableHeadCell>
                  </TableHead>
                  <TableBody className="divide-y">
                    {group.items.map((pd) => (
                      <TableRow key={pd.id}>
                        <TableCell className="whitespace-nowrap font-medium text-gray-900">
                          {pd.deliverable?.name}
                        </TableCell>
                        <TableCell>{pd.project_type}</TableCell>
                        <TableCell className="inline-flex">
                          <Button
                            size="xs"
                            color={selectedDeliverable === pd.id ? 'blue' : 'light'}
                            onClick={() => loadHistory(pd)}
                          >
                            Ver modificaciones
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          );
        })}
      </div>

      <Modals
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        title={selectedDeliverableName ? `Historial de ${selectedDeliverableName}` : 'Historial de modificaciones'}
        modalSize="5xl"
      >
        {history.length === 0 ? (
          <p className="text-sm text-gray-500">
            No hay modificaciones para este entregable.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableHeadCell>Mergetag</TableHeadCell>
                <TableHeadCell>Versión</TableHeadCell>
                <TableHeadCell>Valor</TableHeadCell>
                <TableHeadCell>Fecha</TableHeadCell>
              </TableHead>
              <TableBody className="divide-y">
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="whitespace-nowrap font-medium text-gray-900">
                      {h.mergetag?.name}
                    </TableCell>
                    <TableCell>{h.version}</TableCell>
                    <TableCell className="text-xs text-gray-700">
                      {h.value}
                    </TableCell>
                    <TableCell>
                      {new Date(h.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Modals>
    </div>
  );
};

export default ProjectDetail;
